import 'vite/modulepreload-polyfill';
import '../styles/main.css';
import initCallback from './callback.js';

const onReady = (fn) => {
  if (document.readyState !== 'loading') fn();
  else document.addEventListener('DOMContentLoaded', fn);
};

onReady(() => {
  initCallback();
  // ──────────────────────────────────────────────────────────────────────
  // Mega-menu (прототипная шапка: кнопка каталога + выпадающая панель).
  // ──────────────────────────────────────────────────────────────────────
  document.querySelectorAll('[data-mega-toggle]').forEach((toggle) => {
    const header = toggle.closest('[data-site-header]');
    if (!header) return;
    const panel = header.querySelector('[data-mega-panel]');
    const closeMega = (restoreFocus) => {
      if (!panel || panel.hidden) return;
      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      if (restoreFocus) toggle.focus();
    };
    toggle.addEventListener('click', () => {
      const willOpen = panel.hidden;
      // Закрываем мобильное меню, если оно открыто.
      header.querySelectorAll('[data-mobile-menu]').forEach((m) => {
        if (!m.hidden) m.hidden = true;
      });
      toggle.setAttribute('aria-expanded', String(willOpen));
      panel.hidden = !willOpen;
    });
    document.addEventListener('click', (event) => {
      if (panel.hidden) return;
      if (panel.contains(event.target) || toggle.contains(event.target)) return;
      closeMega(false);
    });
  });

  // ──────────────────────────────────────────────────────────────────────
  // Mobile menu (открытие/закрытие, фокус-ловушка, Escape).
  // ──────────────────────────────────────────────────────────────────────
  document.querySelectorAll('[data-menu-toggle]').forEach((trigger) => {
    const header = trigger.closest('[data-site-header]');
    if (!header) return;
    const panel = header.querySelector('[data-mobile-menu]');
    if (!panel) return;
    const focusableSelector = 'a[href], button:not([disabled]):not([tabindex="-1"])';
    const closePanel = (restoreFocus) => {
      if (panel.hidden) return;
      trigger.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      document.documentElement.classList.remove('menu-open');
      document.body.classList.remove('menu-open');
      if (restoreFocus) trigger.focus();
    };
    const openPanel = () => {
      // Закрываем мега-меню, если оно открыто.
      header.querySelectorAll('[data-mega-panel]').forEach((m) => {
        if (!m.hidden) m.hidden = true;
      });
      header.querySelectorAll('[data-mega-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));

      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      document.documentElement.classList.add('menu-open');
      document.body.classList.add('menu-open');
      requestAnimationFrame(() => {
        const first = panel.querySelector(focusableSelector);
        if (first) first.focus();
      });
    };
    trigger.addEventListener('click', () => {
      if (panel.hidden) openPanel();
      else closePanel(true);
    });
    panel.querySelectorAll('[data-menu-close]').forEach((btn) => {
      btn.addEventListener('click', () => closePanel(true));
    });
    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = [...panel.querySelectorAll(focusableSelector)];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    panel.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => closePanel(false));
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-mega-toggle]').forEach((toggle) => {
      const header = toggle.closest('[data-site-header]');
      const panel = header ? header.querySelector('[data-mega-panel]') : null;
      if (panel && !panel.hidden) {
        toggle.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
        toggle.focus();
      }
    });
    document.querySelectorAll('[data-menu-toggle]').forEach((trigger) => {
      const panel = trigger.closest('[data-site-header]')?.querySelector('[data-mobile-menu]');
      if (panel && !panel.hidden) {
        trigger.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
        document.documentElement.classList.remove('menu-open');
        document.body.classList.remove('menu-open');
        trigger.focus();
      }
    });
  });

  // Закрываем и мега-меню, и мобильное меню при ресайзе в десктоп.
  const desktopQuery = window.matchMedia('(min-width: 64rem)');
  const handleViewportChange = () => {
    if (desktopQuery.matches) {
      document.querySelectorAll('[data-mobile-menu]').forEach((m) => {
        if (!m.hidden) m.hidden = true;
      });
      document.documentElement.classList.remove('menu-open');
      document.body.classList.remove('menu-open');
    } else {
      document.querySelectorAll('[data-mega-panel]').forEach((p) => {
        if (!p.hidden) p.hidden = true;
      });
      document.querySelectorAll('[data-mega-toggle]').forEach((t) =>
        t.setAttribute('aria-expanded', 'false')
      );
    }
  };
  desktopQuery.addEventListener?.('change', handleViewportChange);

  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const autoplayVideos = [...document.querySelectorAll('video[autoplay]')];
  const playAutoplayVideo = (video) => {
    if (reducedMotionQuery.matches) {
      video.pause();
      return;
    }

    // Safari on iOS is stricter than other browsers about autoplay. Set the
    // properties explicitly before calling play(), even though the matching
    // HTML attributes are present.
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('webkit-playsinline', '');
    const playPromise = video.play();
    playPromise?.catch(() => {});
  };
  const pauseAutoplayVideos = () => {
    autoplayVideos.forEach((video) => video.pause());
  };

  autoplayVideos.forEach((video) => {
    video.addEventListener('canplay', () => playAutoplayVideo(video), { once: true });
    playAutoplayVideo(video);
  });

  if (autoplayVideos.length && 'IntersectionObserver' in window) {
    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) playAutoplayVideo(entry.target);
      });
    });
    autoplayVideos.forEach((video) => videoObserver.observe(video));
  }

  reducedMotionQuery.addEventListener?.('change', (event) => {
    if (event.matches) pauseAutoplayVideos();
    else autoplayVideos.forEach((video) => playAutoplayVideo(video));
  });

  // ──────────────────────────────────────────────────────────────────────
  // FAQ (аккордеон): кнопка раскрывает панель, опционально закрывает других.
  // ──────────────────────────────────────────────────────────────────────
  document.querySelectorAll('[data-faq]').forEach((faq, faqIdx) => {
    const items = [...faq.querySelectorAll('[data-faq-item]')];
    items.forEach((item, itemIdx) => {
      const trigger = item.querySelector('[data-faq-trigger]');
      const panel = item.querySelector('[data-faq-panel]');
      if (!(trigger instanceof HTMLButtonElement) || !panel) return;
      const panelId = panel.id || `faq-panel-${faqIdx}-${itemIdx}`;
      const triggerId = trigger.id || `faq-trigger-${faqIdx}-${itemIdx}`;
      panel.id = panelId;
      trigger.id = triggerId;
      trigger.setAttribute('aria-controls', panelId);
      panel.setAttribute('role', 'region');
      panel.setAttribute('aria-labelledby', triggerId);
      trigger.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      trigger.addEventListener('click', () => {
        const willOpen = trigger.getAttribute('aria-expanded') !== 'true';
        if (willOpen && faq.hasAttribute('data-faq-single')) {
          items.forEach((otherItem) => {
            if (otherItem === item) return;
            const otherTrigger = otherItem.querySelector('[data-faq-trigger]');
            const otherPanel = otherItem.querySelector('[data-faq-panel]');
            if (otherTrigger && otherPanel && otherTrigger.getAttribute('aria-expanded') === 'true') {
              otherTrigger.setAttribute('aria-expanded', 'false');
              otherPanel.hidden = true;
            }
          });
        }
        trigger.setAttribute('aria-expanded', String(willOpen));
        panel.hidden = !willOpen;
      });
    });
  });

  // ──────────────────────────────────────────────────────────────────────
  // Product gallery: thumbnails update the stage image, the stage opens a
  // fullscreen lightbox with prev/next navigation (mouse, touch, keyboard).
  // ──────────────────────────────────────────────────────────────────────
  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const stage = gallery.querySelector('[data-gallery-stage]');
    const mainImg = gallery.querySelector('[data-gallery-main]');
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')].filter((t) => t.tagName === 'BUTTON');
    if (!stage || !mainImg) return;

    const lightbox = gallery.querySelector('[data-gallery-lightbox]');
    const lbImg = lightbox ? lightbox.querySelector('[data-gallery-lightbox-img]') : null;
    const lbCounter = lightbox ? lightbox.querySelector('[data-gallery-counter]') : null;

    const items = thumbs.length ? thumbs.map((thumb, i) => ({
      src: thumb.dataset.gallerySrc || '',
      full: thumb.dataset.galleryFull || thumb.dataset.gallerySrc || '',
      alt: thumb.dataset.galleryAlt || mainImg.alt || '',
      thumb,
      index: i,
    })) : [{
      src: mainImg.dataset.gallerySrc || mainImg.currentSrc || mainImg.src,
      full: mainImg.dataset.galleryFull || mainImg.currentSrc || mainImg.src,
      alt: mainImg.alt || '',
      thumb: null,
      index: 0,
    }];

    let index = items.findIndex((item) => item.src === mainImg.getAttribute('src'));
    if (index < 0) index = 0;

    const prevBtns = [...gallery.querySelectorAll('[data-gallery-prev]')];
    const nextBtns = [...gallery.querySelectorAll('[data-gallery-next]')];
    const closeBtns = lightbox ? [...lightbox.querySelectorAll('[data-gallery-close]')] : [];

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    let lastFocus = null;

    const render = () => {
      const item = items[index];
      if (!item) return;
      if (item.src) mainImg.src = item.src;
      mainImg.alt = item.alt;
      if (lbImg && item.full) {
        if (lbImg.src !== item.full) lbImg.classList.add('is-loading');
        lbImg.src = item.full;
        lbImg.alt = item.alt;
      }
      if (lbCounter) lbCounter.textContent = `${index + 1} / ${items.length}`;
      items.forEach((it) => {
        if (it.thumb) it.thumb.setAttribute('aria-pressed', String(it.index === index));
      });
    };

    const go = (delta) => {
      if (!items.length) return;
      index = (index + delta + items.length) % items.length;
      render();
    };

    const openLightbox = () => {
      if (!lightbox) return;
      lastFocus = document.activeElement;
      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('has-lightbox');
      render();
      const closeBtn = lightbox.querySelector('.product-lightbox__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    };

    const closeLightbox = () => {
      if (!lightbox || lightbox.hidden) return;
      lightbox.hidden = true;
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('has-lightbox');
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }
    };

    const trapTab = (e) => {
      if (e.key !== 'Tab' || lightbox.hidden) return;
      const focusable = [...lightbox.querySelectorAll('button:not([disabled]):not([hidden])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    thumbs.forEach((thumb, i) => {
      thumb.setAttribute('aria-pressed', String(i === index));
      thumb.addEventListener('click', () => {
        const found = items.findIndex((it) => it.thumb === thumb);
        if (found < 0) return;
        index = found;
        render();
      });
    });

    prevBtns.forEach((btn) => btn.addEventListener('click', (e) => {
      e.stopPropagation();
      go(-1);
    }));
    nextBtns.forEach((btn) => btn.addEventListener('click', (e) => {
      e.stopPropagation();
      go(1);
    }));
    closeBtns.forEach((btn) => btn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeLightbox();
    }));

    stage.addEventListener('click', (e) => {
      if (e.target.closest('[data-gallery-prev], [data-gallery-next]')) return;
      openLightbox();
    });
    stage.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openLightbox();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      }
    });

    if (lightbox) {
      if (lbImg) {
        const markLoaded = () => lbImg.classList.remove('is-loading');
        lbImg.addEventListener('load', markLoaded);
        lbImg.addEventListener('error', markLoaded);
      }

      lightbox.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          closeLightbox();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          go(-1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          go(1);
        } else {
          trapTab(e);
        }
      });

      let touchStartX = null;
      lightbox.addEventListener('touchstart', (e) => {
        if (!e.touches.length) return;
        touchStartX = e.touches[0].clientX;
      }, { passive: true });
      lightbox.addEventListener('touchend', (e) => {
        if (touchStartX === null) return;
        const end = e.changedTouches[0]?.clientX;
        if (end === undefined) return;
        const delta = end - touchStartX;
        if (Math.abs(delta) > 40) {
          go(delta > 0 ? -1 : 1);
        }
        touchStartX = null;
      }, { passive: true });
    }

    render();
    if (reduceMotion.matches) {
      gallery.dataset.galleryReducedMotion = 'true';
    }
  });

  // ──────────────────────────────────────────────────────────────────────
  // Toasts
  // ──────────────────────────────────────────────────────────────────────
  document.querySelectorAll('[data-toast]').forEach((toast) => {
    const close = toast.querySelector('[data-toast-close]');
    if (!close) return;
    const dismiss = () => toast.remove();
    close.addEventListener('click', dismiss);
    setTimeout(dismiss, 6000);
  });

  // ──────────────────────────────────────────────────────────────────────
  // Smooth in-page anchor scrolling while respecting reduced motion.
  // ──────────────────────────────────────────────────────────────────────
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    const id = anchor.getAttribute('href').slice(1);
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    anchor.addEventListener('click', (e) => {
      e.preventDefault();
      target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    });
  });

  // ──────────────────────────────────────────────────────────────────────
  // Текущий год в подвале.
  // ──────────────────────────────────────────────────────────────────────
  const currentYear = String(new Date().getFullYear());
  document.querySelectorAll('[data-current-year]').forEach((el) => {
    el.textContent = currentYear;
  });

  // ──────────────────────────────────────────────────────────────────────
  // Подсветка активного пункта навигации по data-page на <body>.
  // ──────────────────────────────────────────────────────────────────────
  const currentPage = document.body.dataset.page;
  if (currentPage) {
    document.querySelectorAll('[data-nav-page]').forEach((link) => {
      if (link.dataset.navPage === currentPage) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

});
